// src/features/patient/care-treatment/utils/noteText.ts

/**
 * Purpose : A note's or record's text as plain lines, exactly as printed, for
 * the places that show it inline (a short note inside its visit, a call's
 * first 3 lines, a "To review" preview). Never rewritten or summarised: the
 * words never change; only a printed paragraph's wrapped lines are joined
 * back into its sentences (reflowText).
 *
 * Contents :
 *
 * * function(htmlToText) : the visible text of a cleaned HTML body
 * * function(reflowText) : a printed paragraph's wrapped lines joined back into sentences
 * * function(noteLines) : a note's non-blank lines, in order
 * * function(noteLineCount) : the server's count, else the lines counted here
 * * function(recordText) : a call's / message's own text, else its note's
 */

import type { Encounter, Note, NoteSection } from "@/features/patient/care-treatment/api/careTimelineTypes";
import { sanitizeNoteHtml } from "@/features/patient/care-treatment/utils/sanitizeNoteHtml";

// function(htmlToText)
/** The visible text of an HTML body: block ends become line breaks; nothing else changes. */
export function htmlToText(html: string): string {
  const withBreaks = sanitizeNoteHtml(html).replace(/<(br|\/p|\/div|\/li|\/tr|\/h[1-6])\b[^>]*>/gi, "$&\n");
  if (typeof DOMParser !== "undefined") {
    return new DOMParser().parseFromString(withBreaks, "text/html").body.textContent ?? "";
  }
  return withBreaks.replace(/<[^>]+>/g, "");
}

/** A printed line this long or longer, cut mid-sentence, was wrapped by the printer's narrow column. */
const WRAPPED_LINE_CHARS = 40;
const ENDS_SENTENCE = /[.:;!?]["')\]]?$/;
const STARTS_ITEM = /^(?:[-•*·]|\d+[.)]\s|[a-z][.)]\s)/;

// function(reflowText)
/**
 * The printer's line breaks inside a sentence removed: a line of
 * WRAPPED_LINE_CHARS or more that doesn't end a sentence (. : ; ! ?),
 * followed by a line that goes on in lower case (not indented, not a list
 * item), is joined to it with a space. Every word stays; short lines, list
 * items, headings, indented lines and blank lines keep their breaks.
 */
export function reflowText(text: string): string {
  const out: string[] = [];
  for (const line of text.split(/\r?\n/)) {
    const previous = out[out.length - 1];
    const joins = previous !== undefined
      && previous.trim().length >= WRAPPED_LINE_CHARS
      && !ENDS_SENTENCE.test(previous.trimEnd())
      && /^[a-z(]/.test(line)
      && !STARTS_ITEM.test(line);
    if (joins) out[out.length - 1] = `${previous.trimEnd()} ${line.trim()}`;
    else out.push(line);
  }
  return out.join("\n");
}

function sectionText(section: NoteSection, format: Note["bodyFormat"]): string {
  if (section.blocks && section.blocks.length > 0) {
    return section.blocks
      .map((block) => (block.type === "text" ? reflowText(block.text) : block.rows.map((row) => row.join("  ")).join("\n")))
      .join("\n");
  }
  return reflowText(format === "html" ? htmlToText(section.body) : section.body);
}

// function(noteLines)
/**
 * The note's non-blank lines in order. With several sections each section's
 * title comes first, as printed.
 *
 * @param note - The note.
 * @returns Its lines.
 */
export function noteLines(note: Pick<Note, "sections" | "bodyFormat">): string[] {
  const several = note.sections.length > 1;
  return note.sections.flatMap((section) => {
    const lines = sectionText(section, note.bodyFormat).split(/\r?\n/).filter((line) => line.trim() !== "");
    return several && section.title && lines.length > 0 ? [section.title, ...lines] : lines;
  });
}

// function(noteLineCount)
export function noteLineCount(note: Note): number {
  return note.lineCount ?? noteLines(note).length;
}

// function(recordText)
/**
 * A call's, message's or letter's text: its own (recordText), else its first
 * note's. "" when it has none, and then nothing is shown.
 *
 * @param encounter - The record.
 * @param ownNotes - The notes linked to it.
 */
export function recordText(encounter: Encounter, ownNotes: Note[]): string {
  const own = encounter.recordText?.text?.trim();
  if (own) return reflowText(own);
  const note = ownNotes.find((n) => n.sections.length > 0);
  return note ? noteLines(note).join("\n") : "";
}
